import {
  Grid,
  Autocomplete,
  Table,
  Card,
  Marker,
  Caption,
  Skeleton,
  IOption,
} from '@design-systems/canvas-ui'
import { FavoriteButton } from '@plm-core/favorites'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { UserContext } from 'App/UserContext'
import { useLazyQuery, useQuery } from '@apollo/client/react'
import { debounce } from 'lodash'
import {
  useAllCurrentCoreFilters,
  CoreFilterDropdown,
  CoreFilterMultiselect,
  CoreClearAllFiltersButton,
  CurrentCoreFilterChips,
  CoreFilterAutocompleteSearchDropdown,
  useConjunctiveQueryString,
} from '@plm-core/sort-and-filter'
import {
  ReactRouterCanvasAnchor,
  ContextualizedPagination,
  useContextualizedUpdateTotals,
  usePageLoadErrorToast,
  useContextualizedPaging,
} from '@plm-core/utils'
import {
  SEARCH_TEAMS_QUERY,
  SEARCH_TEAMS_AUTOCOMPLETE,
} from 'graphql/queries/teams/teamsQueries'
import useBusinessPartnersAutocompleteSetup from 'hooks/useBusinessPartnersAutocompleteSetup'

const FREE_TEXT_OPTION_PREFIX = 'free-text:'

type TeamAutocompleteOption =
  | (IOption & { type: 'network' })
  | (IOption & { type: 'free-text'; searchText: string })

const mapQueryToAutocompleteOption = (
  query: string,
): IOption & { type: 'free-text'; searchText: string } => {
  const optionIdentity = `${FREE_TEXT_OPTION_PREFIX}${query}`

  return {
    value: optionIdentity,
    label: `Search for "${query}"`,
    id: optionIdentity,
    type: 'free-text',
    searchText: query,
  }
}

const DEBOUNCE_MS = 300

const getQueryStringForInternal = (searchQuery: string) => {
  const queryString = `(teamType:INTERNAL OR teamType:EXTERNAL) AND ${searchQuery}*`
  return queryString
}

const getQueryStringForExternal = (searchQuery: string, partnerId: string) => {
  const queryString = `(teamType:INTERNAL OR (teamType:EXTERNAL AND partnerId:${partnerId})) AND ${searchQuery}*`
  return queryString
}

const defaultQuery = `(teamType:INTERNAL OR teamType:EXTERNAL)`

const customColumnDefs = [
  {
    id: 'teamName',
    header: 'Team name',
    isRowHeader: true,
    show: true,
    // isSortable: true,
    isResizable: true,
    cell: ({ row }: { row: any }) => {
      const item = row.original
      return (
        <ReactRouterCanvasAnchor to={`/teams/${item.id}`}>
          <Caption below={item.id}>
            <strong>{item.teamName}</strong>
          </Caption>
        </ReactRouterCanvasAnchor>
      )
    },
  },
  {
    id: 'company',
    header: 'Company',
    show: true,
    // isSortable: true,
    isResizable: true,
    cell: ({ row }: { row: any }) => {
      const item = row.original
      return (
        <span>
          {item.teamType === 'EXTERNAL'
            ? (item.businessPartner?.name ?? 'N/A')
            : 'Target'}
        </span>
      )
    },
  },
  {
    id: 'status',
    header: 'Status',
    show: true,
    // isSortable: true,
    width: 50,
    isResizable: true,

    cell: ({ row }: { row: any }) => {
      const item = row.original
      return (
        <Marker
          color={item.status?.description === 'Active' ? 'green' : 'grey'}
        >
          {item.status?.description || 'N/A'}
        </Marker>
      )
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    show: true,
    width: 50,
    isResizable: true,
    cell: ({ row }: { row: any }) => {
      const item = row.original
      return <FavoriteButton entityId={item.id} entityType="team" />
    },
  },
]

// solve this better later
const membershipFilterSetup = {
  label: 'Membership',
  id: 'membership',
  fieldId: 'members.userProfileId',
}

const statusFilterSetup = {
  label: 'Status',
  id: 'status',
  fieldId: 'status.code',
}

const TeamsListBody = () => {
  const [sortingState, setSortingState] = useState()
  const { isInternal, sparkUser } = useContext(UserContext)
  const currentUserId = sparkUser?.userProfileIdString || ''

  const [searchQuery, setSearchQuery] = useState<TeamAutocompleteOption | null>(
    null,
  )
  const { currentPage, pageSize, goToFirstPage } = useContextualizedPaging()
  const currentFilters = useAllCurrentCoreFilters()
  // skipping membership because the NOT makes it special
  const { queryStringMap } = useConjunctiveQueryString([
    'status.code',
    'businessPartner.partnerId',
  ])

  const { handleBusinessPartnerSearchChange } =
    useBusinessPartnersAutocompleteSetup()

  const selectedMembershipFilters = useMemo(() => {
    return currentFilters.filter(
      (filter) => filter.fieldId === 'members.userProfileId',
    )
  }, [currentFilters])

  const getQueryStringForSearch = useMemo(() => {
    let query = defaultQuery
    if (searchQuery) {
      if (searchQuery.type === 'free-text') {
        query += ` AND (teamName:${searchQuery.searchText}*)`
      } else {
        query += ` AND id:${searchQuery.id}`
      }
    }
    if (queryStringMap['status.code']) {
      query += ` AND ${queryStringMap['status.code']}`
    }
    if (queryStringMap['businessPartner.partnerId']) {
      query += ` AND ${queryStringMap['businessPartner.partnerId']}`
    }
    if (selectedMembershipFilters.length > 0) {
      const membershipQuery = selectedMembershipFilters
        .map((filter) => {
          if (filter.value === `!${currentUserId}`) {
            return `NOT members.userProfileId:${currentUserId}`
          } else {
            return `members.userProfileId:${currentUserId}`
          }
        })
        .join(' OR ')
      query += ` AND ${membershipQuery}`
    }
    return query
  }, [searchQuery, queryStringMap, selectedMembershipFilters, currentUserId])

  const [currentQueryStringForSearch, setCurrentQueryStringForSearch] =
    useState(getQueryStringForSearch)

  useEffect(() => {
    if (getQueryStringForSearch !== currentQueryStringForSearch) {
      goToFirstPage()
      setCurrentQueryStringForSearch(getQueryStringForSearch)
    }
  }, [getQueryStringForSearch, currentQueryStringForSearch, goToFirstPage])

  const { data, loading, error } = useQuery(SEARCH_TEAMS_QUERY, {
    variables: {
      page: currentPage,
      q: currentQueryStringForSearch,
      size: pageSize,
    },
  })

  const [fetchTeamsSuggestions] = useLazyQuery(SEARCH_TEAMS_AUTOCOMPLETE, {
    fetchPolicy: 'no-cache',
  })

  const debouncedTeamsFetch = useMemo(
    () =>
      debounce(async (query: string, resolve: (options: IOption[]) => void) => {
        const queryString = isInternal
          ? getQueryStringForInternal(query)
          : getQueryStringForExternal(query, 'partnerId')

        try {
          const { data } = await fetchTeamsSuggestions({
            variables: { q: queryString },
          })
          const results = data?.searchTeams?.content || []
          const options: (IOption & { type?: string })[] = results.map(
            (item) => ({
              value: item.id,
              label: item.teamName,
              id: item.id,
              type: 'network',
            }),
          )
          options.unshift(mapQueryToAutocompleteOption(query))
          resolve(options)
        } catch (error) {
          console.error('Error fetching teams suggestions:', error)
          resolve([mapQueryToAutocompleteOption(query)])
        }
      }, DEBOUNCE_MS),
    [isInternal, fetchTeamsSuggestions],
  )

  const handleSearchChange = useCallback(
    (searchText: string) =>
      new Promise<(IOption & { type?: string })[]>((resolve) => {
        const sanitized = searchText?.trim() || ''
        if (sanitized.length < 3) {
          resolve([mapQueryToAutocompleteOption(sanitized)])
          return
        }
        debouncedTeamsFetch(sanitized, resolve)
      }),
    [debouncedTeamsFetch],
  )

  const teamsList = data?.searchTeams?.content || []

  usePageLoadErrorToast({
    error,
    toastConfig: {
      variant: 'error',
      heading: 'Unable to load teams',
      message:
        'We are unable to load the teams at this time. Please try again or contact support.',
    },
  })

  useContextualizedUpdateTotals({
    totalElements: data?.searchTeams?.pageInfo?.totalElements,
    totalPages: data?.searchTeams?.pageInfo?.totalPages,
  })

  return (
    <Grid.Container>
      <Grid.Item size={{ xs: 12 }}>
        <Grid.Container align="center">
          <Grid.Item size={{ xs: 3 }}>
            <Autocomplete
              placeholder="Search"
              options={handleSearchChange}
              renderOption={({ option }: any) => {
                if (option.type === 'free-text') {
                  return option.label
                }
                return (
                  <Grid.Container>
                    <Grid.Item size={{ xs: 6 }}>{option.label}</Grid.Item>
                    <Grid.Item size={{ xs: 6 }}>{option.id}</Grid.Item>
                  </Grid.Container>
                )
              }}
              value={searchQuery as any}
              onChange={(option: any) => setSearchQuery(option)}
            />
          </Grid.Item>
          <Grid.Item size={{ xs: true }}>
            <Grid.Container>
              <Grid.Item>
                <CoreFilterDropdown {...membershipFilterSetup}>
                  <CoreFilterMultiselect
                    {...membershipFilterSetup}
                    isSearchHidden={true}
                    options={[
                      {
                        value: `${currentUserId}`,
                        label: 'Includes me',
                      },
                      {
                        value: `!${currentUserId}`,
                        label: 'Does not include me',
                      },
                    ]}
                  />
                </CoreFilterDropdown>
              </Grid.Item>
              <Grid.Item>
                {isInternal && (
                  <CoreFilterAutocompleteSearchDropdown
                    label="Company"
                    id="businessPartner"
                    fieldId="businessPartner.partnerId"
                    options={handleBusinessPartnerSearchChange}
                  />
                )}
              </Grid.Item>
              <Grid.Item>
                <CoreFilterDropdown {...statusFilterSetup}>
                  <CoreFilterMultiselect
                    {...statusFilterSetup}
                    isSearchHidden={true}
                    options={[
                      {
                        value: '1',
                        label: 'Active',
                      },
                      {
                        value: '0',
                        label: 'Inactive',
                      },
                    ]}
                  />
                </CoreFilterDropdown>
              </Grid.Item>
            </Grid.Container>
          </Grid.Item>
        </Grid.Container>
      </Grid.Item>
      {currentFilters.length > 0 && (
        <Grid.Item size={{ xs: 12 }}>
          <Grid.Container align="center">
            <Grid.Item>
              <CurrentCoreFilterChips />
            </Grid.Item>
            <Grid.Item>
              <CoreClearAllFiltersButton variant="ghost" />
            </Grid.Item>
          </Grid.Container>
        </Grid.Item>
      )}
      <Grid.Item size={{ xs: 12 }}>
        <ContextualizedPagination />
      </Grid.Item>
      <Grid.Item size={{ xs: 12 }}>
        <Card variant="elevated" className="hc8-pa-4x">
          {!loading && (
            <Table
              name="Teams list"
              alternateRowColor
              data={{
                columnDefs: customColumnDefs,
                rowData: teamsList,
              }}
              mode="server"
              sorting={{
                sortingState: sortingState as any,
                onSortingChange: (newSortingState) =>
                  setSortingState(newSortingState as any),
              }}
              hasGrid={false}
              isCornerRounded
              highlightOnHover="row"
            />
          )}
          {loading && <Skeleton variant="rectangle" height="800px" />}
        </Card>
      </Grid.Item>
      <Grid.Item size={{ xs: 12 }}>
        <ContextualizedPagination />
      </Grid.Item>
    </Grid.Container>
  )
}

export default TeamsListBody
